import { Injectable } from '@angular/core';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ITablePagination, PMOrderOperation } from 'src/app/models/domain';
import { PmOrderOperationDataService } from 'src/app/services/pm-order-operation-data.service';
import { handleError } from 'src/app/state/akita-error-helper';
import { ErrorState } from 'src/app/state/error-state';
import { IPage } from 'src/app/state/paged-collection';

/**
 * Deliberately not `providedIn: 'root'`. Two components use it — planning-report and
 * development-milestones-frontend-table — and for the `Reorder` role they render in the same
 * tab, so a shared instance would surface one table's error under both. Listed in each
 * component's `providers` alongside its paginator.
 */
@Injectable()
export class PMOperationService {
  private readonly state = new ErrorState();

  public readonly error$ = this.state.error$;

  public constructor(private readonly pmOperationDataService: PmOrderOperationDataService) {}

  public getPage(
    pmOrderIds: string[],
    pagination: ITablePagination
  ): Observable<IPage<PMOrderOperation>> {
    // Starting a load clears the previous error, so a transient SAP failure does not
    // leave the error view up permanently — the templates render it instead of the table.
    this.state.setError(undefined);

    if (pmOrderIds.length === 0) {
      return of({
        currentPage: pagination.page,
        data: [],
        lastPage: 1,
        perPage: pagination.perPage,
        total: 0,
      });
    }

    return this.pmOperationDataService.getPaginatedPMOperation(pmOrderIds, pagination).pipe(
      map(({ data, total }): IPage<PMOrderOperation> => ({
        currentPage: pagination.page,
        data,
        lastPage: Math.ceil(total / pagination.perPage),
        perPage: pagination.perPage,
        total,
      })),
      catchError((error) => {
        handleError(this.state, error);

        return EMPTY;
      })
    );
  }
}
